import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { BrandIcon } from '@components/BrandIcon';
import googleIcon from '@iconify-icons/logos/google-icon';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { GoogleCalendarService, GoogleCalendarStatus } from '../services/google-calendar.service';

const GOOGLE_CONNECT_CHANNEL = 'crm.google-agenda';

interface GoogleConnectResult {
    ok: boolean;
    detail: string;
}

interface Props {
    status: GoogleCalendarStatus | null;
    onChanged: () => void;
}

/// Cada vendedor conecta a própria agenda; fica desligado se o servidor não tem o app do Google.
export const AgendaGoogleConnect = ({ status, onChanged }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const [searchParams, setSearchParams] = useSearchParams();

    const announce = (result: GoogleConnectResult) => {
        if (result.ok) showAlert(t('googleAgenda.conectada', { conta: result.detail }), 'success', 3000);
        else showAlert(result.detail || t('googleAgenda.falhou'), 'error', 5000);

        onChanged();
    };

    // O login do Google roda em outra aba; a aba do CRM fica onde estava e só recebe o resultado.
    useEffect(() => {
        // A aba da volta do Google é quem envia; ouvir aqui avisaria duas vezes.
        if (searchParams.get('google')) return undefined;

        const channel = new BroadcastChannel(GOOGLE_CONNECT_CHANNEL);

        channel.onmessage = (event: MessageEvent<GoogleConnectResult>) => announce(event.data);

        return () => channel.close();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A volta do Google cai na aba do login com ?google=ok|erro: repassa para o CRM e fecha.
    useEffect(() => {
        const google = searchParams.get('google');

        if (!google) return;

        const result: GoogleConnectResult = { ok: google === 'ok', detail: searchParams.get('detalhe') ?? '' };
        const channel = new BroadcastChannel(GOOGLE_CONNECT_CHANNEL);

        channel.postMessage(result);
        channel.close();
        window.close();

        // Aberta sem ser pelo botão, o navegador não deixa fechar: avisa aqui mesmo.
        searchParams.delete('google');
        searchParams.delete('detalhe');
        setSearchParams(searchParams, { replace: true });
        announce(result);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!status) return null;

    const connect = async () => {
        // Abre a aba já no clique: depois do await o navegador trataria como pop-up e bloquearia.
        const tab = window.open('', '_blank');

        if (!tab) {
            showAlert(t('googleAgenda.popupBloqueado'), 'warning', 5000);
            return;
        }

        // O Google não precisa de acesso à aba do CRM.
        tab.opener = null;

        try {
            const res = await new GoogleCalendarService().connect();
            const answer = res.data.data;

            if (answer?.ready && answer.url) {
                tab.location.href = answer.url;
                return;
            }

            tab.close();
            showAlert(answer?.reason ?? t('googleAgenda.falhou'), 'warning', 4000);
        } catch (err) {
            tab.close();
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const disconnect = async () => {
        try {
            await new GoogleCalendarService().disconnect();
            showAlert(t('googleAgenda.desconectada'), 'success', 2500);
            onChanged();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const buttonSx = (color: string) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: 43,
        px: 1.6,
        borderRadius: '9px',
        fontSize: 12.5,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        color: cc.onTint,
        border: `1px solid ${color}66`,
        bgcolor: `${color}22`,
        userSelect: 'none',
        '& .MuiSvgIcon-root': { color },
    });

    // Sem o app do Google no servidor: mostra o botão desligado e diz o motivo, em vez de sumir.
    if (!status.available) {
        return (
            <Tooltip title={t('googleAgenda.naoConfiguradaAjuda')}>
                <Box
                    aria-disabled
                    sx={{ ...buttonSx(cc.textMuted), cursor: 'not-allowed', opacity: 0.6 }}
                >
                    <BrandIcon icon={googleIcon} size={16} />
                    {t('googleAgenda.conectar')}
                </Box>
            </Tooltip>
        );
    }

    if (!status.connected || status.expired) {
        const expired = status.connected && status.expired;

        return (
            <Tooltip title={expired ? t('googleAgenda.expiradaAjuda') : t('googleAgenda.conectarAjuda')}>
                <Box
                    role="button"
                    tabIndex={0}
                    onClick={connect}
                    onKeyDown={(e) => e.key === 'Enter' && connect()}
                    sx={buttonSx(expired ? cc.eventWarning : cc.accent)}
                >
                    <BrandIcon icon={googleIcon} size={16} />
                    {expired ? t('googleAgenda.reconectar') : t('googleAgenda.conectar')}
                </Box>
            </Tooltip>
        );
    }

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                height: 43,
                pl: 1.4,
                pr: 0.5,
                borderRadius: '9px',
                border: `1px solid ${cc.border}`,
                bgcolor: cc.surfaceBg,
            }}
        >
            <BrandIcon icon={googleIcon} size={16} />
            <Typography noWrap sx={{ fontSize: 12.5, color: cc.textPrimary, maxWidth: 220 }}>
                {status.email}
            </Typography>
            <Tooltip title={t('googleAgenda.desconectar')}>
                <IconButton size="small" onClick={disconnect} sx={{ color: cc.textMuted }}>
                    <LinkOffIcon sx={{ fontSize: '17px !important' }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};
